import { useState } from 'react';
import { TextField, Button, Typography, Paper } from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AddIcon from '@mui/icons-material/Add';
import { Tag } from '../../stores/TagsStore';
import { useTranslations } from 'next-intl';

interface TaskInputProps {
  onAddTask: (description: string, selectedTags: Tag[]) => void;
  onOpenTagDialog: () => void;
  selectedTags: Tag[];
}

export const TaskInput = ({ onAddTask, onOpenTagDialog, selectedTags }: TaskInputProps) => {
  const t = useTranslations('TaskInput');
  const [newTask, setNewTask] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newTask.trim() && newTask.trim().length > 0) {
      onAddTask(newTask.trim(), selectedTags);
      setNewTask('');
      setError(false);
    } else {
      setError(true);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewTask(e.target.value);
    if (error) {
      setError(false);
    }
  };

  return (
    <div className="flex gap-2 content-center items-center">
      <div className="flex-1">
        <TextField
          placeholder={error ? t('emptyError') : t('placeholder')}
          value={newTask}
          onChange={handleChange}
          className="w-full bg-card rounded-full"
          error={error}
          sx={{
            input: {
              padding: '8px 16px',
              color: 'var(--color-foreground)',
              '&::placeholder': {
                color: 'var(--color-muted-foreground)',
                opacity: 0.6,
              },
            },
            '& .MuiOutlinedInput-root': {
              borderRadius: '150px',
              transition: 'border-color 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
              '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: '4px',
                borderColor: 'var(--color-border)',
                transition: 'border-color 0.3s ease-in-out',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--color-primary)',
              },
              '&.Mui-focused': {
                boxShadow: '0 0 0 2px rgba(var(--color-primary-rgb), 0.2)',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'var(--color-primary)',
                },
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderWidth: '4px'
              },
              '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--color-error)',
              },
            },
            '& .MuiFormHelperText-root': {
              marginLeft: '16px',
              '&.Mui-error': {
                color: 'var(--color-error)',
              },
            },
          }}
        />
      </div>

      <Button
        onClick={onOpenTagDialog}
        className='rounded-full bg-button-surface hover:bg-primary-light w-10 h-10 min-w-0 p-0 flex items-center justify-center transition-colors relative'
        title={selectedTags.length > 0 ? t('selectedTags', { count: selectedTags.length }) : t('selectTags')}
        sx={{
          backgroundColor: 'var(--color-button-surface)',
        }}
      >
        <LocalOfferIcon className="h-4 w-4" sx={{
          color: 'var(--color-primary)',
        }} />
        {selectedTags.length > 0 && (
          <Paper className="absolute -top-1 -right-1 text-sm rounded-full w-5 h-5 flex items-center justify-center" sx={{
            backgroundColor: 'var(--color-button-surface)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            boxShadow: 'none',
          }}>
            <Typography sx={{
              fontSize: '0.75rem',
              lineHeight: 1,
              margin: 0,
              fontWeight: 500,
              color: 'var(--color-primary)',
            }}>
              {selectedTags.length}
            </Typography>
          </Paper>
        )}
      </Button>

      <Button
        onClick={handleSubmit}
        type="submit"
        className="rounded-full hover:bg-primary-light w-10 h-10 min-w-0 p-0 flex items-center justify-center transition-colors"
        sx={{
          backgroundColor: 'var(--color-button-surface)',
        }}
      >
        <AddIcon className="h-6 w-6" sx={{
          color: 'var(--color-primary)',
        }} />
      </Button>
    </div>
  );
};